import { permissionsApi } from "../../../components/PermissionsManagement/services/permissionsApi";
import { buildRequestBody } from "./utils/apiRequestBuilder";

/**
 * Handles save and cancel actions for permissions
 * Uses a config object to avoid circular dependencies
 */
export const usePermissionsActions = ({
  rawApiData,
  currentPermissions,
  trackedChanges,
  selectedGroup,
  setSaving,
  clearError,
  setError,
  saveChanges,
  resetState,
  hasAnyChanges,
  updateRawDataFromTrackedChanges,
}) => {
  /**
   * Save permission changes to the API
   * @param {string} source - Where the save was triggered from: 'toolbar', 'button', or 'modal'
   */
  const handleSave = async (source = "button") => {
    if (!currentPermissions || !selectedGroup) {
      return;
    }

    setSaving(true, source);
    clearError();

    try {
      // No need to filter here - trackedChanges should already be clean
      // The cleanup happens when items are unchecked via sync

      const requestBody = buildRequestBody(
        trackedChanges, // Pass trackedChanges directly
        selectedGroup.id,
        rawApiData,
        currentPermissions
      );

      if (Object.keys(requestBody).length > 0) {
        await permissionsApi.savePermissions(
          requestBody,
          selectedGroup.id,
          selectedGroup.name
        );
      }

      // Update the raw data with saved changes
      // This makes the current saved state the new baseline for comparisons
      updateRawDataFromTrackedChanges(trackedChanges);

      // Clear ALL tracked changes after updating the baseline
      saveChanges();
    } catch (error) {
      console.error("Save failed:", error);
      setError(error.message || "Failed to save permissions");
      throw error;
    } finally {
      setSaving(false, source);
    }
  };

  /**
   * Cancel changes and optionally confirm with user
   * @param {boolean} skipConfirmation - Skip confirmation dialog
   */
  const handleCancel = () => {
    resetState();
    clearError();
  };

  return {
    handleSave,
    handleCancel,
  };
};
